import React from "react";
import "./VisitRewardScreen.css";
import GTLogoEmblem from "../assets/brand/GTLogoEmblem.svg";
import RewardTicketClock from "../assets/brand/RewardTicketClock.svg";
import useCountdown from "../hooks/useCountdown";

const pad = (value) => String(value).padStart(2, "0");

const SPARKS = [
  { left: "46%", delay: "0s" },
  { left: "58%", delay: "0.4s" },
  { left: "34%", delay: "0.8s" },
  { left: "68%", delay: "1.2s" },
];

/** "8:00 AM, Sat 10 Oct" in IST, so the copy never says an ambiguous "tomorrow". */
export function formatUnlockTime(unlockAtIso) {
  const date = new Date(unlockAtIso);
  if (Number.isNaN(date.getTime())) return "";
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-IN", {
      timeZone: "Asia/Kolkata",
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
      .formatToParts(date)
      .map((part) => [part.type, part.value])
  );
  return `${parts.hour}:${parts.minute} ${String(parts.dayPeriod || "").toUpperCase()}, ${parts.weekday} ${parts.day} ${parts.month}`;
}

const istDayKey = (date) =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(date);

/** True when the unlock moment falls on the calendar day after `nowIso` (IST). */
export function isUnlockTomorrow(unlockAtIso, nowIso) {
  const unlock = new Date(unlockAtIso);
  const now = nowIso ? new Date(nowIso) : new Date();
  if (Number.isNaN(unlock.getTime()) || Number.isNaN(now.getTime())) return false;
  return istDayKey(unlock) === istDayKey(new Date(now.getTime() + 24 * 60 * 60 * 1000));
}

/**
 * Shown inside the checkout sheet after the customer taps Done on a visit 1-4
 * order. The header's close button leaves the screen. It shows what the reward is and when it lands, but never a coupon:
 * the coupon is only created by the morning job, so the ticket stays locked.
 */
export default function VisitRewardScreen({
  reward,
  unlockAt,
  serverNow,
  isRegisteredAppUser,
  appUrl,
}) {
  const { hours, minutes, seconds, isComplete } = useCountdown(unlockAt, serverNow);
  const unlockLabel = formatUnlockTime(unlockAt);
  const tomorrow = isUnlockTomorrow(unlockAt, serverNow);

  return (
    <div className="vrs">
      <section className="vrs-card" aria-labelledby="vrs-title">
        <div className="vrs-hero">
          <div className="vrs-sparks" aria-hidden="true">
            {SPARKS.map((spark, index) => (
              <span key={index} style={{ left: spark.left, animationDelay: spark.delay }} />
            ))}
          </div>
          <span className="vrs-eyebrow">🎁 Your visit reward</span>
          <h2 id="vrs-title" className="vrs-title">Your reward is on its way</h2>
          <p className="vrs-sub">
            {isRegisteredAppUser
              ? `It appears in your Got Tea app ${tomorrow ? "tomorrow morning" : "when the timer ends"}.`
              : `It appears in the Got Tea app ${tomorrow ? "tomorrow morning" : "when the timer ends"}. Sign in with this number.`}
          </p>
          <div className="vrs-emblem" aria-hidden="true">
            <img src={GTLogoEmblem} alt="" />
          </div>
        </div>

        <div className="vrs-body">
          <div className="vrs-orange-box">
            <div className="vrs-ticket">
              <img className="vrs-ticket-bg" src={RewardTicketClock} alt="" />
              <div className="vrs-ticket-text">
                <p className="vrs-ticket-desc">{reward.description}</p>
              </div>
              {reward.validityDays ? (
                <span className="vrs-pill">Valid {reward.validityDays} days</span>
              ) : null}
            </div>
          </div>

          <div className="vrs-timer" aria-live="polite">
            <p className="vrs-timer-label">
              {isComplete ? "Your reward should be in the app now" : "Unlocks in"}
            </p>
            {!isComplete && (
              <div className="vrs-chips">
                <div><strong>{pad(hours)}</strong><span>hrs</span></div>
                <div><strong>{pad(minutes)}</strong><span>min</span></div>
                <div><strong>{pad(seconds)}</strong><span>sec</span></div>
              </div>
            )}
            {unlockLabel && !isComplete && <p className="vrs-timer-at">{tomorrow ? `Tomorrow at ${unlockLabel.split(",")[0]}` : `at ${unlockLabel}`}</p>}
          </div>

          <a className="vrs-cta" href={appUrl} target="_blank" rel="noopener noreferrer">
            {isRegisteredAppUser ? "Open the app" : "Get the app"}
          </a>
        </div>
      </section>
    </div>
  );
}
